import useTitanApp from "@/hooks/useTitanApp";
import { useState, useEffect } from "react";
import {
  addPatientAllergies,
  getPatientAllergies,
  addAllergyOptimistic,
  removeAllergyById,
  deletePatientAllergies,
} from "@/dispatchers/patientDispatchers";

export interface Allergy {
  allergyName: string;
  severity: "mild" | "moderate" | "severe";
  reaction: string;
  notes: string;
}

const mapSeverity = (severity: number): "mild" | "moderate" | "severe" => {
  switch (severity) {
    case 1:
      return "mild";
    case 2:
      return "moderate";
    case 3:
      return "severe";
    default:
      return "mild";
  }
};

const mapSeverityToNumber = (
  severity: "mild" | "moderate" | "severe",
): number => {
  switch (severity) {
    case "mild":
      return 1;
    case "moderate":
      return 2;
    case "severe":
      return 3;
    default:
      return 1;
  }
};

export const defaultAllergies: Allergy[] = [
  {
    allergyName: "Hayfever",
    severity: "mild",
    reaction: "Sneezing, runny nose",
    notes: "",
  },
  {
    allergyName: "Penicillin",
    severity: "severe",
    reaction: "Skin rash, breathing difficulty",
    notes: "Documented allergy since childhood",
  },
  {
    allergyName: "Aspirin",
    severity: "moderate",
    reaction: "Stomach upset",
    notes: "",
  },
];

export const allergySeverityOptions = [
  { value: "mild", label: "Mild", color: "green" },
  { value: "moderate", label: "Moderate", color: "orange" },
  { value: "severe", label: "Severe", color: "red" },
] as const;

export const commonAllergies = [
  "Penicillin",
  "Aspirin",
  "Ibuprofen",
  "Codeine",
  "Morphine",
  "Latex",
  "Hayfever",
  "Dust mites",
  "Pet dander",
  "Food allergies",
  "Shellfish",
  "Nuts",
  "Eggs",
  "Milk",
];

export const useAllergiesState = () => {
  const { patientState } = useTitanApp();
  const {
    isPatientDetailsLoading,
    selectedPatientDetails,
    patientAllergies,
    patientAllergiesLoading,
  } = patientState || {};
  const { patientID } = selectedPatientDetails || {};

  const DashboardPatientAllergies: Allergy[] =
    patientAllergies?.map((a, index) => ({
      id: String(index + 1), // if backend has no id, fallback to index
      name: a.allergyName || "",
      severity: mapSeverity(a.severity),
      reaction: a.typicalReaction || "",
      notes: a.additionalNotes || "",
    })) || [];

  return {
    isPatientDetailsLoading,
    patientAllergies,
    DashboardPatientAllergies,
    patientID,
    patientAllergiesLoading,
  };
};

export const savePatientAllergy = async (allergy, patientId) => {
  if (!patientId) {
    console.error("No patientId provided for savePatientAllergy");
    return;
  }

  const tempId = `temp-${Date.now()}`;

  // const uiAllergy = {
  //   id: tempId,
  //   allergyName: allergy.name,
  //   severity: allergy.severity,
  //   typicalReaction: allergy.reaction,
  //   additionalNotes: allergy.notes,
  // };

  // // Optimistic update
  // addAllergyOptimistic(uiAllergy);

  try {
    const formattedAllergy = {
      allergyName: allergy.name,
      severity: allergy.severity,
      typicalReaction: allergy.reaction,
      additionalNotes: allergy.notes,
    };

    const patientAllergies = {
      patientId,
      allergies: [formattedAllergy],
    };

    await addPatientAllergies(patientAllergies);

    await getPatientAllergies(patientId);
  } catch (err) {
    console.error("Failed to save allergy:", err);

    removeAllergyById(tempId);
  }
};

export const updatePatientAllergy = async (
  index: number,
  updatedAllergy: Allergy,
  patientId: string,
  existingAllergies: any[],
) => {
  if (!patientId) {
    console.error("No patientId provided for updatePatientAllergy");
    return;
  }

  try {
    const oldAllergy = existingAllergies[index]; // grab current allergy at that position

    // Map both old and new to backend format
    const formattedOld = {
      allergyName: oldAllergy.allergyName ?? oldAllergy.name,
      severity: oldAllergy.severity,
      typicalReaction: oldAllergy.typicalReaction ?? oldAllergy.reaction,
      additionalNotes: oldAllergy.additionalNotes ?? oldAllergy.notes,
    };

    const formattedNew = {
      allergyName: updatedAllergy.allergyName,
      severity: updatedAllergy.severity,
      typicalReaction: updatedAllergy.reaction,
      additionalNotes: updatedAllergy.notes,
    };

    const payload = {
      patientId,
      allergies: [formattedOld, formattedNew], // backend expects 2 objects
    };

    await addPatientAllergies(payload);
    await getPatientAllergies(patientId);
  } catch (err) {
    console.error("Failed to update allergy:", err);
  }
};

export const deletePatientAllergy = async (
  allergyToDelete: any,
  patientId: string,
) => {
  if (!patientId) {
    console.error("No patientId provided for deletePatientAllergy");
    return;
  }

  try {
    // Map to backend format
    const formattedAllergy = {
      allergyName: allergyToDelete.allergyName ?? allergyToDelete.name,
      severity:
        typeof allergyToDelete.severity === "string"
          ? mapSeverityToNumber(allergyToDelete.severity)
          : allergyToDelete.severity,
      typicalReaction:
        allergyToDelete.typicalReaction ?? allergyToDelete.reaction,
      additionalNotes: allergyToDelete.additionalNotes ?? allergyToDelete.notes,
    };

    const payload = {
      patientId,
      allergies: [formattedAllergy], // backend expects array
    };

    await deletePatientAllergies(payload);
    await getPatientAllergies(patientId);
  } catch (err) {
    console.error("Failed to delete allergy:", err);
  }
};
